import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Radio, DollarSign, Users, Activity, Loader2, TrendingUp, Info, Cpu, Database, Gauge } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";

function money(sym, n) {
  return `${sym}${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function Card({ icon: Icon, label, value, sub, testid, delay, info }) {
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay }}
      className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }}
    >
      <div className="flex items-center justify-between">
        <Icon className="w-5 h-5 text-primary-c" />
        <div className="flex items-center gap-1.5">
          {info && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button data-testid={`${testid}-info`} aria-label="What is this?" className="opacity-60 hover:opacity-100 transition-opacity">
                  <Info className="w-3.5 h-3.5" style={{ color: "var(--text-2)" }} />
                </button>
              </TooltipTrigger>
              <TooltipContent className="glass max-w-[240px] text-xs" data-testid={`${testid}-tip`}>{info}</TooltipContent>
            </Tooltip>
          )}
          {sub && <span className="text-[10px] mono uppercase tracking-wider glass rounded-full px-2 py-0.5" style={{ color: "var(--text-2)" }}>{sub}</span>}
        </div>
      </div>
      <div className="font-display font-black text-3xl tracking-tighter mt-3">{value}</div>
      <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>{label}</div>
    </motion.div>
  );
}

const SYNC_EXPLAINER =
  "Cloud Sync Health estimates how reliably organizer sessions are publishing snapshots to the cloud over the last 24–48h. 80%+ (green) means live views, resume and spectator links are all up to date; 40–79% (amber) means some sessions are running local-only and haven't synced recently; below 40% (red) indicates widespread offline play or connectivity issues.";

const RANGES = [
  { v: "7d", l: "7 days" },
  { v: "30d", l: "30 days" },
  { v: "90d", l: "90 days" },
];

function statusMeta(status) {
  switch (status) {
    case "healthy": return { color: "#22c55e", label: "Healthy", pct: 100 };
    case "busy": return { color: "#eab308", label: "Busy", pct: 66 };
    case "degraded": return { color: "#f97316", label: "Degraded", pct: 40 };
    default: return { color: "#ef4444", label: "Down", pct: 10 };
  }
}

function PerformanceGauge() {
  const [health, setHealth] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/health").then(({ data }) => setHealth(data)).catch(() => setHealth(false));
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const meta = health && health !== false ? statusMeta(health.status) : statusMeta("down");
  const latency = health && health !== false ? health.db_latency_ms : null;

  return (
    <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="performance-gauge">
      <div className="flex items-center justify-between mb-4">
        <span className="flex items-center gap-2 font-display font-bold"><Gauge className="w-4 h-4 text-primary-c" /> Live Performance</span>
        <span className="text-[10px] mono uppercase tracking-wider glass rounded-full px-2 py-0.5 flex items-center gap-1" style={{ color: "var(--text-2)" }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: meta.color }} /> live · 5s
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl p-3 text-center" style={{ background: "var(--bg)" }} data-testid="perf-app">
          <Cpu className="w-4 h-4 mx-auto text-primary-c" />
          <div className="font-display font-black text-lg mt-1.5" style={{ color: "#22c55e" }}>Online</div>
          <div className="text-[10px] mt-0.5" style={{ color: "var(--text-2)" }}>App server</div>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ background: "var(--bg)" }} data-testid="perf-db">
          <Database className="w-4 h-4 mx-auto text-primary-c" />
          <div className="font-display font-black text-lg mt-1.5" style={{ color: meta.color }} data-testid="perf-db-status">{meta.label}</div>
          <div className="text-[10px] mt-0.5" style={{ color: "var(--text-2)" }}>Database</div>
        </div>
        <div className="rounded-xl p-3 text-center" style={{ background: "var(--bg)" }} data-testid="perf-latency">
          <Activity className="w-4 h-4 mx-auto text-primary-c" />
          <div className="font-display font-black text-lg mt-1.5" data-testid="perf-latency-value">{latency == null ? "—" : `${latency}`}<span className="text-xs">ms</span></div>
          <div className="text-[10px] mt-0.5" style={{ color: "var(--text-2)" }}>DB latency</div>
        </div>
      </div>
      <div className="h-2 rounded-full overflow-hidden mt-4" style={{ background: "var(--bg)" }}>
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${meta.pct}%`, background: meta.color }} />
      </div>
    </div>
  );
}

function GrowthChart() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/admin/user-growth?range=${range}`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [range]);

  return (
    <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="growth-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <span className="flex items-center gap-2 font-display font-bold"><TrendingUp className="w-4 h-4 text-primary-c" /> User Growth</span>
        <div className="glass rounded-full p-0.5 flex gap-0.5" data-testid="growth-range-filter">
          {RANGES.map((r) => (
            <button key={r.v} data-testid={`growth-range-${r.v}`} onClick={() => setRange(r.v)}
              className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${range === r.v ? "text-[var(--bg)]" : ""}`}
              style={range === r.v ? { background: "var(--primary)" } : { color: "var(--text-2)" }}>{r.l}</button>
          ))}
        </div>
      </div>
      {data === null ? (
        <div className="h-56 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary-c" /></div>
      ) : data === false ? (
        <p className="text-sm py-16 text-center" style={{ color: "var(--text-2)" }}>Could not load growth data.</p>
      ) : (
        <>
          <div className="flex gap-6 mb-3 text-sm">
            <span className="mono"><span className="text-primary-c font-bold text-lg">{data.total_users}</span> <span style={{ color: "var(--text-2)" }}>total users</span></span>
            <span className="mono"><span className="text-primary-c font-bold text-lg">+{data.series.reduce((a, b) => a + b.new, 0)}</span> <span style={{ color: "var(--text-2)" }}>in range</span></span>
          </div>
          <div style={{ width: "100%", height: 220 }} data-testid="growth-chart-canvas">
            <ResponsiveContainer>
              <AreaChart data={data.series} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="growthFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "var(--text-2)" }} tickFormatter={(d) => d.slice(5)} minTickGap={24} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "var(--text-2)" }} allowDecimals={false} axisLine={false} tickLine={false} width={40} />
                <RTooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 12 }}
                  labelStyle={{ color: "var(--text-2)" }} />
                <Area type="monotone" dataKey="total" name="Total users" stroke="var(--primary)" strokeWidth={2} fill="url(#growthFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminTelemetry() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/telemetry").then(({ data }) => setData(data)).catch(() => setData(false));
  }, []);

  if (data === null) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;
  if (data === false) return <p className="text-sm" style={{ color: "var(--text-2)" }}>Could not load telemetry.</p>;

  const health = data.sync_health;
  const healthColor = health >= 80 ? "#22c55e" : health >= 40 ? "#eab308" : "#ef4444";

  return (
    <TooltipProvider delayDuration={100}>
      <div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="telemetry-cards">
          <Card icon={Radio} label="Active events" value={data.active_events} sub="live" testid="telemetry-active-events" delay={0} />
          <Card icon={DollarSign} label={`MRR (${data.currency})`} value={money(data.currency_symbol, data.mrr)} sub="recurring" testid="telemetry-mrr" delay={0.05} />
          <Card icon={Users} label="Registered users" value={data.total_users} sub="total" testid="telemetry-users" delay={0.1} />
          <Card icon={Activity} label="System sync health" value={`${health}%`} sub="24-48h" testid="telemetry-sync" delay={0.15} info={SYNC_EXPLAINER} />
        </div>

        <div className="mt-5 rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="telemetry-health-bar">
          <div className="flex items-center justify-between text-sm mb-3">
            <span className="flex items-center gap-2 font-display font-bold">
              <TrendingUp className="w-4 h-4 text-primary-c" /> Cloud Sync Health
              <Tooltip>
                <TooltipTrigger asChild>
                  <button data-testid="sync-health-info" aria-label="Explain Cloud Sync Health" className="opacity-60 hover:opacity-100 transition-opacity">
                    <Info className="w-3.5 h-3.5" style={{ color: "var(--text-2)" }} />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="glass max-w-[280px] text-xs leading-relaxed" data-testid="sync-health-tip">{SYNC_EXPLAINER}</TooltipContent>
              </Tooltip>
            </span>
            <span className="mono" style={{ color: healthColor }}>{health}% healthy</span>
          </div>
          <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "var(--bg)" }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${health}%`, background: healthColor }} />
          </div>
          <p className="text-xs mt-3" style={{ color: "var(--text-2)" }}>
            {data.club_pro_licenses} active Club Pro license(s) · {data.total_events} total events tracked
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <PerformanceGauge />
          <GrowthChart />
        </div>
      </div>
    </TooltipProvider>
  );
}
